// The card request: six screens of a few questions each, over the film (Film.tsx). The questions, saving and checks
// come from the server (pr/card/fields.py); this file is only the look and the pacing.
import { Fragment, useEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react'
import type { Answers, FieldDef, StepDef } from './types'
import { CardFilm } from './Film'
import { track, trackGone } from './track'

export const answered = (v: unknown) => !(v === undefined || v === null || v === '' || v === false || (Array.isArray(v) && !v.length))
/** Answered for this question: a Yes/No's "No" is an answer; only a box to tick needs it ticked (founder, 2026-11-02). */
export const answeredFor = (f: FieldDef, v: unknown) => (f.kind === 'yes_no' ? v === true || v === false : answered(v))

/** The film stop for each screen (Film.tsx STOPS): arrival, the fan, the tunnel, the tile rows, the reveal. The last
 *  screen (where they heard about us) holds on the reveal too: no new film. */
const SCREEN_STOP: Record<string, number> = { you: 0, company: 1, business: 2, card: 3, people: 4, source: 4 }

/** A question over a set of lines that only make sense together. */
const HEADING: Record<string, string> = { rep_first_name: "Who runs the company's finances?" }

/** Titles that make the applicant the person who runs the finances: the same rule as the server (fields.is_exec). */
const EXEC_TITLE = /\b(ceo|cfo|coo|chief\b.*\bofficer|co-?founder|founder|(?<!product )(?<!process )(?<!data )owner|president|managing (member|partner|director)|general partner|proprietor|treasurer)\b/i
export function withDerived(answers: Answers): Answers {
  const raw = answers.applicant_title
  if (typeof raw !== 'string' || !raw.trim()) return answers
  const t = raw.trim().replace(/\s+/g, ' ')      // the server stores the title with spaces squeezed
  return { ...answers, representative_is_applicant: EXEC_TITLE.test(t) && !/\b(vice|vp|assistant|asst)\b/i.test(t) }
}

/** A step's questions split into screens: the questions that share a group share a screen, in order. */
export function screensOf(fields: FieldDef[]): FieldDef[][] {
  const out: FieldDef[][] = []
  for (const f of fields) {
    if (f.derived) continue
    const last = out[out.length - 1]
    if (last && last[0].group === f.group) last.push(f)
    else out.push([f])
  }
  return out
}

/** Every screen's group across the steps, in order: a screen's place here is its stop in the film and its number. */
export function screenGroups(steps: StepDef[]): string[] {
  const out: string[] = []
  for (const s of steps) for (const f of s.fields) if (!f.derived && !out.includes(f.group || f.key)) out.push(f.group || f.key)
  return out
}

/** A few related questions per screen (founder, 2026-11-02: five screens, three or four questions each, so the
 *  browser can autofill a whole screen). Enter or Continue checks every answer on the screen, then moves on. */
export function FocusFlow(p: {
  steps: StepDef[]; index: number; fields: FieldDef[]; answers: Answers
  setError: (key: string, msg: string) => void
  input: (f: FieldDef, picked: () => void) => ReactNode
  onFinishStep: () => void | Promise<void>
  onBackStep?: () => void
  /** Checks one answer before moving on: null when it's fine, else the sentence to show under it. */
  check?: (f: FieldDef) => Promise<string | null>
  saveLine: ReactNode
  finishLabel: string
  footer?: ReactNode
  busy?: boolean
  focusKey?: string | null
  startAtEnd?: boolean
  welcome?: { title: string; sub: string } | null
  /** Every screen's group in order, across the steps the person will see (screenGroups). */
  groups?: string[]
  /** Keys whose answer is kept on file (the EIN): an empty box there is not a missing answer. */
  onFile?: string[]
}) {
  const { steps, index, fields, answers } = p
  const screens = screensOf(fields)
  const groupOf = (s: FieldDef[] | undefined) => (s ? s[0].group || s[0].key : null)
  const [key, setKey] = useState<string | null>(null)
  const [welcome, setWelcome] = useState(!!p.welcome)
  const [dir, setDir] = useState<'fwd' | 'back'>('fwd')
  const stepKey = steps[index].key
  useEffect(() => { setKey(groupOf(p.startAtEnd ? screens[screens.length - 1] : screens[0])) }, [stepKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const home = screens.find((s) => s.some((f) => f.key === p.focusKey))
    if (p.focusKey && home) { setKey(groupOf(home)); setWelcome(false) }
  }, [p.focusKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const at = Math.max(0, screens.findIndex((s) => groupOf(s) === key))
  const screen = screens[at]
  const single = screen?.length === 1
  const lastQ = at === screens.length - 1
  const lastStep = index === steps.length - 1
  const wantNext = useRef(false)
  const [checking, setChecking] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const next = async () => {
    if (!screen || checking) return
    const bad: Record<string, string> = {}
    for (const f of screen) {
      if (f.required && !answeredFor(f, answers[f.key]) && !p.onFile?.includes(f.key)) bad[f.key] = f.ask || 'Answer this to go on.'
    }
    if (!Object.keys(bad).length && p.check) {
      setChecking(true)
      try {
        for (const f of screen) {
          if (!answeredFor(f, answers[f.key]) && f.kind !== 'people') continue
          const why = await p.check(f)
          if (why) bad[f.key] = why
        }
      } finally { setChecking(false) }
    }
    const first = screen.find((f) => bad[f.key])
    if (first) {
      for (const f of screen) if (bad[f.key]) p.setError(f.key, bad[f.key])
      requestAnimationFrame(() => {
        const box = formRef.current?.querySelector<HTMLElement>(`[data-testid="card-${first.key}"], [data-testid^="card-${first.key}-"] input, [data-testid^="card-${first.key}-"] select`)
        box?.focus({ preventScroll: true })
      })
      return
    }
    setDir('fwd')
    track(lastQ && lastStep ? 'send' : 'screen_done')
    if (!lastQ) setKey(groupOf(screens[at + 1]))
    else await p.onFinishStep()
  }
  const back = () => {
    track('back')
    setDir('back')
    if (at > 0) setKey(groupOf(screens[at - 1]))
    else p.onBackStep?.()
  }
  // a tile pick moves on by itself, but only when it's the screen's one question
  useEffect(() => {
    if (wantNext.current && single && answeredFor(screen[0], answers[screen[0].key])) {
      wantNext.current = false
      const t = window.setTimeout(() => { void next() }, 320)
      return () => window.clearTimeout(t)
    }
  }, [answers]) // eslint-disable-line react-hooks/exhaustive-deps

  // The first box on each new screen takes focus, so typing and Enter work without a click (founder, 2026-11-02).
  useEffect(() => {
    if (welcome) return
    const el = formRef.current?.querySelector<HTMLElement>('input:not([type="hidden"]):not([disabled]), select, textarea')
    el?.focus({ preventScroll: true })
  }, [key, welcome, stepKey])
  // Enter anywhere on the page means Continue (a tile, the page itself); inside a text box the form handles it.
  const nextRef = useRef(next)
  nextRef.current = next
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t?.closest('textarea, button, a, [contenteditable="true"]')) return
      // a text box submits its form on Enter by itself; a checkbox, a choice or a list doesn't, so do it here
      if (t?.closest('form.fx-q') && t.matches('input:not([type]), input[type="text"], input[type="email"], input[type="tel"], input[type="url"], input[type="number"], input[type="search"]')) return
      if (t?.matches('select') && e.defaultPrevented) return
      if (welcome) { e.preventDefault(); track('start'); setWelcome(false); return }
      e.preventDefault()
      void nextRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [welcome])

  const groups = p.groups ?? []
  const g = Math.max(0, groups.indexOf(groupOf(screen) ?? ''))
  // Where people stop (track.ts): each screen as it shows, numbered from the groups list, and the question in use.
  const shown = welcome ? 'welcome' : screen ? groupOf(screen) : null
  useEffect(() => {
    if (shown) track(shown === 'welcome' ? 'welcome' : 'screen_view', { screen: shown, screen_no: shown === 'welcome' ? 0 : g + 1 })
  }, [shown]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => trackGone, [])
  const focused = useRef<string | null>(null)
  const onFocus = (e: FocusEvent) => {
    const f = (e.target as HTMLElement).closest('[data-field]')?.getAttribute('data-field') ?? null
    if (f && f !== focused.current) { focused.current = f; track('field_focus', { field: f, screen: groupOf(screen), screen_no: g + 1 }) }
  }
  const total = Math.max(groups.length, 1)
  const progress = Math.min(1, g / total)
  // a pick on the last screen never sends by itself: Send does (the source screen is one question of tiles)
  const picked = () => { if (single && !(lastQ && lastStep)) wantNext.current = true }
  const skippable = !!screen && screen.every((f) => !f.required && !answeredFor(f, answers[f.key])) && !(lastQ && lastStep)
  return (
    <div className="fx">
      <CardFilm pose={welcome || !screen ? -1 : SCREEN_STOP[groupOf(screen) ?? ''] ?? Math.min(g, 4)} />
      <div className="fx-top">
        <div className="fx-bar" data-testid="card-steps" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((welcome ? 0 : progress) * 100)}><span style={{ width: `${(welcome ? 0 : progress) * 100}%` }} /></div>
        <div className="fx-meta">
          <span>{welcome ? '' : `${g + 1} of ${total}`}</span>
          <span className="fx-save">{p.saveLine}</span>
        </div>
      </div>
      <div className="fx-body">
        {welcome && p.welcome ? (
          <div className="fx-welcome">
            <h1>{p.welcome.title}</h1>
            <p>{p.welcome.sub}</p>
            <button type="button" className="fx-go" data-testid="card-begin" onClick={() => { track('start'); setWelcome(false) }} autoFocus>Start <span aria-hidden="true">→</span></button>
            <p className="fx-hint">About 3 minutes</p>
          </div>
        ) : screen ? (
          <form key={`${stepKey}-${groupOf(screen)}`} ref={formRef} className={`fx-q fx-${dir}${single ? '' : ' fx-multi'}`}
            data-kind={single ? screen[0].kind : 'screen'} data-testid={`card-form-${stepKey}`} data-question={screen[0].key}
            data-screen={groupOf(screen) ?? ''} onFocus={onFocus} onSubmit={(e) => { e.preventDefault(); void next() }}>
            {screen.map((f) => (
              <Fragment key={f.key}>
                {HEADING[f.key] ? <h2 className="fx-head">{HEADING[f.key]}</h2> : null}
                <div className="fx-one" data-field={f.key}>{p.input(f, picked)}</div>
              </Fragment>
            ))}
            {lastQ && lastStep && p.footer ? <div className="fx-footer" data-testid="card-send">{p.footer}</div> : null}
            <div className="fx-actions">
              <button type="submit" className={`fx-go${checking ? ' fx-checking' : ''}`} disabled={p.busy} aria-busy={checking}
                data-testid={lastQ && lastStep ? 'card-submit' : 'card-next'}>
                {lastQ && lastStep ? p.finishLabel : 'Continue'} <span aria-hidden="true">→</span>
              </button>
              {skippable ? <button type="button" className="fx-skip" data-testid="card-skip-question" onClick={() => { track('skip'); setDir('fwd'); if (!lastQ) setKey(groupOf(screens[at + 1])); else void p.onFinishStep() }}>Skip</button> : null}
              <span className="fx-hint">or press Enter ↵</span>
            </div>
            {at > 0 || p.onBackStep ? <button type="button" className="fx-back" data-testid="card-back" onClick={back}>← Back</button> : null}
          </form>
        ) : null}
      </div>
    </div>
  )
}
